import { LoadingButton } from '@mui/lab';
import { useState } from 'react';

import {
  useAioInitializeHubspotMutation,
  useAioInitializeWorkflowMutation,
} from 'src/api/apigQuery';

import { FormikTextField } from '../../../common/formik-material-ui/FormikTextField';

export interface AIOSettings {
  intentFieldName: string;
  intentFieldLabel: string;
  intentScoreThreshold: number;
}

const AIOSettingsTab = ({ formik, userAtom, enqueueSnackbarWithStyles }) => {
  const [aioInitializeHubspotMutation] = useAioInitializeHubspotMutation();
  const [aioInitializeWorkflowMutation] = useAioInitializeWorkflowMutation();
  const [isInitializingHubspot, setIsInitializingHubspot] = useState(false);
  const [isInitializingWorkflow, setIsInitializingWorkflow] = useState(false);
  const onInitializeHubspot = async () => {
    try {
      setIsInitializingHubspot(true);
      const response = await aioInitializeHubspotMutation({
        userSettings: {
          businessId: userAtom.businessid,
          userid: userAtom.userid,
          apikey: userAtom.apikey,
        },
      });
      if (response?.data?.success) {
        enqueueSnackbarWithStyles({
          message:
            response?.data?.message || 'MaestroAI initialized successfully!',
          alertProps: { severity: 'success' },
        });
      } else {
        throw new Error(
          response?.error?.data?.error ||
            response?.data?.message ||
            'Failed to initialize MaestroAI. Please try again.'
        );
      }
    } catch (error) {
      enqueueSnackbarWithStyles({
        message:
          error?.message ||
          'An unexpected error occurred while initializing AIO.',
        alertProps: { severity: 'error' },
      });
    } finally {
      setIsInitializingHubspot(false);
    }
  };

  const onInitializeWorkflow = async () => {
    try {
      setIsInitializingWorkflow(true);
      const response = await aioInitializeWorkflowMutation({
        userSettings: {
          businessId: userAtom.businessid,
          userid: userAtom.userid,
          apikey: userAtom.apikey,
        },
      }).unwrap();
      if (response?.success) {
        enqueueSnackbarWithStyles({
          message:
            response?.message ?? 'Hubspot workflow initialized successfully!',
          alertProps: { severity: 'success' },
        });
      } else {
        throw new Error(
          response?.error ??
            'Failed to initialize Hubspot workflow. Please try again.'
        );
      }
    } catch (e) {
      console.log('Initialize Hubspot workflow error', e);
      enqueueSnackbarWithStyles({
        message:
          'An unexpected error occurred while initializing Hubspot workflow.',
        alertProps: { severity: 'error' },
      });
    } finally {
      setIsInitializingWorkflow(false);
    }
  };

  return (
    <div>
      <div className="row borderBottom">
        <div className="col-lg-8">
          <h5 className="fieldTitle">Initialize MaestroAI</h5>
          <p className="fieldDescription">
            Click the button to MaestroAI. This will create the necessary CRM
            MaestroAI properties and fields.
          </p>
        </div>
        <div className="col-lg-4">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              height: '100%',
            }}
          >
            <LoadingButton
              className="superStyle"
              disableElevation
              data-qa="aio-initialize-hubspot"
              variant="contained"
              color="primary"
              style={{ height: '36px', marginTop: '8px' }}
              disabled={isInitializingHubspot || isInitializingWorkflow}
              loading={isInitializingHubspot || isInitializingWorkflow}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await onInitializeHubspot();
                await onInitializeWorkflow();
              }}
            >
              Initialize AIO
            </LoadingButton>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-8">
          <h5 className="fieldTitle">Intent Score Threshold</h5>
          <p className="fieldDescription">
            Minimum intent score (0–100) required to proceed, If a contacts
            score is below this value, the Decision Engine does nothing and logs
            why. Example: 85 means only scores ≥ 85 advance.
          </p>
        </div>
        <div className="col-lg-4">
          <FormikTextField
            name="aioSettings.intentScoreThreshold"
            formik={formik}
            style={{
              marginRight: '8px',
              flexGrow: 1,
              width: '100%',
            }}
            label="Intent Score Threshold"
            size="small"
            type="number"
            InputProps={{
              inputProps: {
                min: 0,
                max: 100,
              },
            }}
            variant="outlined"
          />
        </div>
      </div>
    </div>
  );
};

export default AIOSettingsTab;
